import type { PayloadAction } from '@reduxjs/toolkit';
import { createAction, createSlice } from '@reduxjs/toolkit';

import type { FilterObjectData, FilterOptions } from '@app/components/CustomFilter';
import formatDateWithTimeZone from '@app/utils/formatDateWithTimeZone/formatDateWithTimeZone';

import type {
    Contributor,
    EditContributors,
    GetContributors,
    GetTasks,
    PutTasks,
    Task,
    TaskStatus,
    User,
} from './types';

const initialState = {
    tasks: {
        ongoing: {
            content: [] as Task[],
            count: 0,
            limit: 0,
            offset: 0,
            total: 0,
            filteredTotal: 0,
        },
        queued: {
            content: [] as Task[],
            count: 0,
            limit: 0,
            offset: 0,
            total: 0,
            filteredTotal: 0,
        },
        closed: {
            content: [] as Task[],
            count: 0,
            limit: 0,
            offset: 0,
            total: 0,
            filteredTotal: 0,
        },
    },
    countsByStatuses: {
        completed: 1,
        ongoing: 1,
        queued: 1,
    },
    tasksLoading: true,
    taskTags: [] as string[],
    tasksTagsLoading: false,
    taskEditLoading: false,
    contributorsLoading: false,
    contributors: <Contributor[]>[],
    searchOrFilterResultsShown: false,
    searchOrFilterTasksLoading: false,
    searchValue: '',
    filterOptions: <FilterOptions[]>[],
    filterValue: <FilterObjectData[]>[],
};

const tasksSlice = createSlice({
    name: 'tasks',
    initialState,
    reducers: {
        setTasks: (state, { payload }: PayloadAction<{ ongoing?: GetTasks; queued?: GetTasks; closed?: GetTasks }>) => {
            state.tasks = {
                ...state.tasks,
                ...payload,
            };
        },
        setQueriedTasks: (state, { payload }: PayloadAction<{ content: Task[]; status: TaskStatus; page: number }>) => {
            const { content, status, page } = payload;

            // first page comes fresh because of pageSize change
            if (page === 1) {
                state.tasks[status].content = content;

                return;
            }

            state.tasks[status].content = state.tasks[status].content.concat(content);
        },
        setTasksLoading: (state, { payload }: PayloadAction<boolean>) => {
            state.tasksLoading = payload;
        },
        setTasksFilterOptions: (state, { payload }: PayloadAction<FilterOptions[]>) => {
            state.filterOptions = payload;
        },
        setTasksFilterValue: (state, { payload }: PayloadAction<FilterObjectData[]>) => {
            state.filterValue = payload;
        },
        setTasksSearchValue: (state, { payload }: PayloadAction<string>) => {
            state.searchValue = payload;
        },
        setSearchOrFilterTasksLoading: (state, { payload }: PayloadAction<boolean>) => {
            state.searchOrFilterTasksLoading = payload;
        },
        setSearchOrFilterResultsShown: (state, { payload }: PayloadAction<boolean>) => {
            state.searchOrFilterResultsShown = payload;
        },
        setTaskTags: (state, { payload }: PayloadAction<string[]>) => {
            state.taskTags = payload;
        },
        setTaskTagsLoading: (state, { payload }: PayloadAction<boolean>) => {
            state.tasksTagsLoading = payload;
        },
        setCloseTask: (state, { payload }: PayloadAction<{ taskId: number; status: TaskStatus; user: User }>) => {
            const { status, taskId, user } = payload;

            const changeToClosed = (task: Task): Task => ({
                ...task,
                status: 'closed',
                completedBy: user,
                // .replace() for safari to understand this date format
                completedDate: formatDateWithTimeZone(new Date(), 'yyyy-MM-dd HH:mm:ss').replace(' ', 'T'),
            });

            state.tasks[status].total -= 1;
            state.tasks[status].count -= 1;
            state.tasks.closed.total += 1;
            state.tasks.closed.count += 1;

            state.tasks.queued.content = state.tasks.queued.content.filter((task) => {
                if (task.parentTask?.id === taskId) {
                    state.tasks.queued.total -= 1;
                    state.tasks.ongoing.total += 1;

                    const updatedTask: Task = { ...task, status: 'ongoing' };

                    state.tasks.ongoing.content.unshift(updatedTask);
                }

                return task.parentTask?.id !== taskId;
            });

            state.tasks[status].content = state.tasks[status].content.filter((task) => {
                if (task.id === taskId) {
                    const updatedTask = changeToClosed(task);

                    state.tasks.closed.content.unshift(updatedTask);
                }

                return task.id !== taskId;
            });
        },
        setCloseLanguage: (
            state,
            { payload }: PayloadAction<{ taskId: number; langId: number; taskStatus: TaskStatus }>,
        ) => {
            const { taskStatus, taskId, langId } = payload;

            const taskStatuses = Object.keys(state.tasks) as TaskStatus[];

            const reviewTask = taskStatuses
                .map((status) => state.tasks[status].content)
                .flat()
                .find(({ parentTask }) => parentTask?.id === taskId);

            if (reviewTask) {
                const { status } = reviewTask;

                if (status === 'queued') {
                    state.tasks.queued.total -= 1;
                    state.tasks.ongoing.total += 1;

                    reviewTask.status = 'ongoing';

                    state.tasks.ongoing.content.unshift(reviewTask);

                    state.tasks.queued.content = state.tasks.queued.content.filter(
                        (task) => taskId !== task.parentTask?.id,
                    );
                }

                reviewTask.targetLanguages = reviewTask.targetLanguages.map((language) => {
                    const languageClone = { ...language };

                    if (languageClone.language.id === langId) {
                        languageClone.status = 'created';
                    }

                    return languageClone;
                });
            }

            state.tasks[taskStatus].content = state.tasks[taskStatus].content.map((task) => {
                if (task.id === taskId) {
                    // eslint-disable-next-line no-param-reassign
                    task.targetLanguages = task.targetLanguages.map((language) => {
                        if (language.language.id === langId) {
                            // eslint-disable-next-line no-param-reassign
                            language.status = 'completed';
                        }

                        return language;
                    });
                }

                return task;
            });
        },
        setDeleteTask: (state, { payload }: PayloadAction<{ taskId: number; status: TaskStatus }>) => {
            const { status, taskId } = payload;

            state.tasks[status].total -= 1;
            state.tasks[status].count -= 1;

            state.tasks[status].content = state.tasks[status].content.filter((task) => task.id !== taskId);
        },
        editTask: (state, { payload }: PayloadAction<{ editData: PutTasks; status: TaskStatus }>) => {
            const { status, editData } = payload;

            state.tasks[status].content = state.tasks[status].content.map((task) => {
                if (task.id === editData.taskId) {
                    return {
                        ...task,
                        ...payload.editData,
                    };
                }

                return task;
            });
        },
        setContributorsLoading: (state, { payload }: PayloadAction<boolean>) => {
            state.contributorsLoading = payload;
        },
        setContributors: (state, { payload }: PayloadAction<Contributor[]>) => {
            state.contributors = payload;
        },
        setLanguageContributors: (state, { payload }: PayloadAction<EditContributors>) => {
            const { contributors, taskId, langId, taskStatus } = payload;

            state.tasks[taskStatus].content = state.tasks[taskStatus].content.map((task) => {
                if (task.id === taskId) {
                    // eslint-disable-next-line no-param-reassign
                    task.targetLanguages = task.targetLanguages.map((language) => {
                        if (language.language.id === langId) {
                            // eslint-disable-next-line no-param-reassign
                            language.contributors = contributors;
                        }

                        return language;
                    });
                }

                return task;
            });
        },
    },
});

const getTasks = createAction('Tasks/GET_TASKS', (payload: { projectId: string; branch: string }) => ({
    payload,
}));

const getQueriedTasks = createAction(
    'Tasks/GET_QUERIED_TASKS',
    (payload: {
        projectId: string;
        query: TaskStatus;
        branch: string;
        page: number;
        pageSize?: number;
        search: string;
        filters: FilterObjectData[];
    }) => ({
        payload,
    }),
);

const getTaskTags = createAction('Tasks/GET_TASK_TAGS', (projectId: string) => ({
    payload: { projectId },
}));

const putTask = createAction('Tasks/PUT_TASK', (payload: { editData: PutTasks; status: TaskStatus }) => ({
    payload,
}));

const closeLanguage = createAction(
    'Tasks/CLOSE_LANGUAGE',
    (payload: { taskId: number; langId: number; taskStatus: TaskStatus }) => ({
        payload,
    }),
);

const closeTask = createAction(
    'Tasks/CLOSE_TASK',
    (payload: {
        taskId: number;
        projectId: string;
        status: TaskStatus;
        user: User;
        statusLength: number;
        statusTotal: number;
        branchId: string | null;
        branch: string;
    }) => ({
        payload,
    }),
);

const deleteTask = createAction(
    'Tasks/DELETE_TASK',
    (payload: {
        taskId: number;
        projectId: string;
        status: TaskStatus;
        statusLength: number;
        statusTotal: number;
        branchId: string | null;
        branch: string;
    }) => ({
        payload,
    }),
);

const initialReport = createAction('Tasks/INITIAL_REPORT', (payload: number) => ({
    payload,
}));

const finalReport = createAction('Tasks/Final_REPORT', (payload: number) => ({
    payload,
}));

const detailedReport = createAction('Tasks/DETAILED_REPORT', (payload: number) => ({
    payload,
}));

const uploadOfflineXLIFF = createAction(
    'Tasks/UPLOAD_OFFLINE_XLIFF',
    (payload: { files: FileList; taskId: number }) => ({
        payload,
    }),
);

const downloadOfflineXLIFF = createAction('Tasks/DOWNLOAD_OFFLINE_XLIFF', (payload: number) => ({
    payload,
}));

const getContributors = createAction('Tasks/GET_CONTRIBUTORS', (payload: GetContributors) => ({
    payload,
}));

const editContributors = createAction('Tasks/EDIT_CONTRIBUTORS', (payload: EditContributors) => ({
    payload,
}));

const getSearchedOrFilteredTasks = createAction(
    'tasks/GET_SEARCHED_TASKS',
    (payload: { search?: string; branch: string; projectId: string; filters: FilterObjectData[] }) => ({
        payload,
    }),
);

const downloadLqaReport = createAction('Tasks/LQA_REPORT_DOWNLOAD', (payload: number) => ({
    payload,
}));

export default tasksSlice.reducer;

export const tasksActions = {
    ...tasksSlice.actions,
    getTasks,
    getQueriedTasks,
    getTaskTags,
    putTask,
    deleteTask,
    closeTask,
    closeLanguage,
    initialReport,
    finalReport,
    detailedReport,
    uploadOfflineXLIFF,
    downloadOfflineXLIFF,
    getContributors,
    editContributors,
    getSearchedOrFilteredTasks,
    downloadLqaReport,
};

export type TasksState = typeof initialState;
